import React, { useCallback, useMemo } from "react";
import { CpDateRangeSelector } from "@components";
import { DateTime } from "luxon";
import { isEmpty } from "lodash";
import { getColumnProp } from "./date-filter.helper";
import {
  ColumnSchema,
  FilterControl,
} from "src/components/cp-data-table/types";
import { FilterSelections } from "src/components/cp-table-header-menu/cp-table-header-menu.types";

const defaultDateFormat = "yyyy-MM-dd";

type DateFilterData = {
  start_date?: string;
  end_date?: string;
  include_empty?: boolean;
  selected_date?: string;
};
function isDateFilterData(value: unknown): value is DateFilterData {
  return typeof value === "object" && value !== null;
}

type DateFilterProps = {
  columnSchema: ColumnSchema;
  filterControl: FilterControl;
  onClose: () => void;
  hideNoDateAdded: boolean;
  dateFormat: string;
  dateRanges: string[];
  relativeDates: boolean;
};

export function DateFilter({
  columnSchema,
  filterControl,
  onClose,
  hideNoDateAdded,
  dateFormat,
  dateRanges = [],
  relativeDates = false,
}: DateFilterProps) {
  if (!columnSchema.filter) {
    throw new Error(
      `Column schema is missing filter.fieldId for column ${columnSchema.id}`,
    );
  }
  const filterField = columnSchema.filter.fieldId;

  const prevFilterData = filterControl.filters[columnSchema.id]?.data?.[0];
  const prevFilter = isDateFilterData(prevFilterData)
    ? prevFilterData
    : undefined;
  const prevSortDir = filterControl.sortData[columnSchema.id]?.direction;

  const column = useMemo(
    () =>
      getColumnProp(filterField, hideNoDateAdded, dateRanges, relativeDates),
    [filterField, hideNoDateAdded, dateRanges, relativeDates],
  );
  const outputDateFormat = dateFormat || defaultDateFormat; // The format that we send to the BE

  const onApply = useCallback(
    (_sortParam: string, selections: FilterSelections | number) => {
      if (typeof selections === "number") {
        return;
      }
      const dates = selections;
      const inputFormat = "MM/dd/yyyy";
      const filter: DateFilterData = {};
      const filterParams =
        typeof dates.filter_params === "string"
          ? dates.filter_params
          : undefined;
      const isFixedDate = !relativeDates || filterParams === "custom";
      if (dates.after && isFixedDate) {
        filter.start_date = DateTime.fromFormat(
          dates.after,
          inputFormat,
        ).toFormat(outputDateFormat);
      }
      if (dates.before && isFixedDate) {
        filter.end_date = DateTime.fromFormat(
          dates.before,
          inputFormat,
        ).toFormat(outputDateFormat);
      }
      if (filterParams === "noDateAdded") {
        filter.include_empty = true;
      }
      if (filterParams) {
        filter.selected_date = filterParams;
      }
      const filterToApply: DateFilterData[] = isEmpty(filter) ? [] : [filter];
      filterControl.applyFilter(columnSchema.id, {
        filterData: filterToApply,
        sortDir: dates.order ?? null,
      });
      onClose();
    },
    [columnSchema.id, filterControl, onClose, outputDateFormat, relativeDates],
  );

  const startDateIso = prevFilter?.start_date
    ? DateTime.fromFormat(prevFilter.start_date, outputDateFormat).toISODate()
    : undefined;
  const endDateIso = prevFilter?.end_date
    ? DateTime.fromFormat(prevFilter.end_date, outputDateFormat).toISODate()
    : undefined;

  const filterSelections: FilterSelections = {
    ...(startDateIso && { after: startDateIso }),
    ...(endDateIso && { before: endDateIso }),
    ...(prevFilter?.selected_date && {
      filter_params: prevFilter.selected_date,
    }),
    ...(prevSortDir && { order: prevSortDir }),
  };

  return (
    <CpDateRangeSelector
      hideSortOptions={!columnSchema.sort}
      column={column}
      filterContext={{
        applyFilter: onApply,
        filters: {
          [filterField]: filterSelections,
        },
      }}
      menuStyles={{ display: "block" }}
      onCancel={onClose}
      setColumnMenuShown={() => {}} // this is supposed to be optional, but it always gets called when clicking Apply
    />
  );
}
DateFilter.dropdownWidth = 500;
